import { useRouter } from '@application/utils';
import type { AnimalSensitivityEnum, MtAnimalBreederGetAnimalQuery } from '@gql/graphql';
import { BirthdayType, GenderEnum, ParentTypeEnum } from '@gql/graphql';
import type { MtAnimalsBreederPageFormValues } from '@presentation/pages';
import dayjs from 'dayjs';
import { useCallback } from 'react';

export const useAnimalInitialValuesBuilder = () => {
  const { infoFromCurrentPath } = useRouter();

  const animalInitialValuesBuilder = useCallback(
    (
      animal?: NonNullable<MtAnimalBreederGetAnimalQuery['accountMe']>['animals'][number],
    ): MtAnimalsBreederPageFormValues => {
      const { parentType } = infoFromCurrentPath.search;
      let initialGender: GenderEnum | undefined;
      if (parentType === ParentTypeEnum.FATHER) {
        initialGender = GenderEnum.M;
      } else if (parentType === ParentTypeEnum.MOTHER) {
        initialGender = GenderEnum.F;
      }

      if (!animal)
        return {
          birthdayType: BirthdayType.DATE,
          birthday: undefined,
          sensitivities: undefined,
          dogInfo: {
            breedId: undefined,
            pedigree: undefined,
            breederIsDogOwner: undefined,
            competitionTitles: undefined,
            healthTestsResults: undefined,
            microshipNumber: undefined,
            pedigreeNumber: undefined,
            color: undefined,
            coat: undefined,
          },
          gender: initialGender,
          images: [undefined],
          name: undefined,
          size: undefined,
          weight: undefined,
          studInfo: {
            availableForBreeding: undefined,
            knownPrice: undefined,
            price: undefined,
            champion: undefined,
            type: undefined,
            adn: undefined,
            accomodation: undefined,
            description: undefined,
          },
        };

      return {
        images: (animal.imgs ?? [])
          .filter((img) => !!img.medium)
          .map((img) => img.medium as string),
        name: animal.name ?? undefined,
        sensitivities: (animal.sensitivities as AnimalSensitivityEnum[]) ?? undefined,
        gender: animal.gender ?? initialGender,
        birthdayType: animal.birthdayType ?? BirthdayType.DATE,
        birthday: animal.birthday ? dayjs(animal.birthday) : undefined,
        passportFile: animal.passportFile ? animal.passportFile : undefined,
        size: animal.size ? animal.size : undefined,
        weight: animal.weight ? animal.weight : undefined,

        ...(animal.dogInfos == null
          ? {
              dogInfo: {
                breedId: undefined,
                pedigree: undefined,
                breederIsDogOwner: undefined,
                competitionTitles: undefined,
                healthTestsResults: undefined,
                microshipNumber: undefined,
                pedigreeNumber: undefined,
                color: undefined,
                coat: undefined,
              },
            }
          : {
              dogInfo: {
                breederIsDogOwner: animal.dogInfos.breederIsDogOwner ?? undefined,
                pedigree:
                  animal.dogInfos.pedigree !== undefined ? animal.dogInfos.pedigree : undefined,
                pedigreeNumber: animal.dogInfos.pedigreeNumber ?? undefined,
                microshipNumber: animal.dogInfos.microshipNumber ?? undefined,
                competitionTitles: animal.dogInfos.competitionTitles ?? undefined,
                healthTestsResults: animal.dogInfos.healthTestsResults ?? undefined,
                breedId: animal.dogInfos.dogBreed?.id ?? undefined,
                color: animal.dogInfos.color ?? undefined,
                coat: animal.dogInfos.coat ?? undefined,
              },
            }),

        ...(animal.studInfos == null
          ? {
              studInfo: {
                availableForBreeding: undefined,
                knownPrice: undefined,
                price: undefined,
                champion: undefined,
                type: undefined,
                adn: undefined,
                accomodation: undefined,
                description: undefined,
              },
            }
          : {
              studInfo: {
                availableForBreeding: animal.studInfos.availableForBreeding ?? undefined,
                knownPrice: animal.studInfos.price !== null,
                price: animal.studInfos.price ?? undefined,
                champion: animal.studInfos.champion ?? undefined,
                type: animal.studInfos.type ?? undefined,
                adn: animal.studInfos.adn ?? undefined,
                accomodation: animal.studInfos.accomodation ?? undefined,
                description: animal.studInfos.description ?? undefined,
              },
            }),
      };
    },
    [infoFromCurrentPath.search],
  );

  return { animalInitialValuesBuilder };
};
